import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import ScribeSessionList from '../components/scribe/ScribeSessionList';
import NoteReview from '../components/scribe/NoteReview';
import SessionTranscript from '../components/scribe/SessionTranscript';
import { formatApptTime, pickApptTime } from '../utils/apptTime';

const STATUS_COLORS = {
  scheduled:    { bg: '#dbeafe', text: '#1e40af', label: 'Scheduled' },
  'checked-in': { bg: '#d1fae5', text: '#065f46', label: 'Checked In' },
  completed:    { bg: '#dcfce7', text: '#166534', label: 'Completed' },
  'no-show':    { bg: '#fef3c7', text: '#92400e', label: 'No-Show' },
  canceled:     { bg: '#fee2e2', text: '#991b1b', label: 'Canceled' },
  rescheduled:  { bg: '#e0e7ff', text: '#3730a3', label: 'Rescheduled' }
};

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function AmbientScribe({ practiceId }) {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState('appointments');
  const [appointments, setAppointments] = useState([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [scribeSessionMap, setScribeSessionMap] = useState({});
  const [stats, setStats] = useState(null);
  // GH-184: search + pagination for the appointment list
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  // Session detail view (from history tab)
  const [selectedSession, setSelectedSession] = useState(null);
  const [detailPane, setDetailPane] = useState('note');
  const [transcript, setTranscript] = useState(null);
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [transcriptError, setTranscriptError] = useState(null);
  const [sessionNotes, setSessionNotes] = useState(null);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesSaving, setNotesSaving] = useState(false);

  // Load today's appointments
  const loadAppointments = useCallback(async () => {
    if (!practiceId) return;
    setAppointmentsLoading(true);
    try {
      const today = todayStr();
      const res = await api.getAppointments(practiceId, {
        startDate: today,
        endDate: today
      });
      const appts = res.data || [];
      setAppointments(appts);

      // GH-184: batch-load ALL of today's scribe sessions in ONE call
      // instead of firing an N+1 (was calling getScribeSessions per
      // appointment_id — 50 appts = 50 sequential requests). Backend
      // supports dateFrom/dateTo query params on /scribe/sessions.
      const sessionMap = {};
      try {
        const sessRes = await api.getScribeSessions(practiceId, {
          dateFrom: today,
          dateTo: today,
          limit: 200,
        });
        for (const s of (sessRes?.data || [])) {
          const apptId = s.appointment_id;
          if (!apptId) continue;
          // If multiple sessions per appointment, keep the most recent
          // (findByAppointment returns ordered DESC, matching prior behavior)
          if (!sessionMap[apptId] || (s.created_at && sessionMap[apptId].created_at < s.created_at)) {
            sessionMap[apptId] = s;
          }
        }
      } catch (e) {
        console.warn('[AmbientScribe] batch scribe session load failed:', e.message);
      }
      setScribeSessionMap(sessionMap);
    } catch (err) {
      console.error('Failed to load appointments:', err);
    } finally {
      setAppointmentsLoading(false);
    }
  }, [practiceId]);

  const loadStats = useCallback(async () => {
    if (!practiceId) return;
    try {
      const res = await api.getScribeStats(practiceId);
      if (res.data) setStats(res.data);
    } catch (err) {
      console.error('Failed to load scribe stats:', err);
    }
  }, [practiceId]);

  useEffect(() => {
    loadAppointments();
    loadStats();
  }, [loadAppointments, loadStats]);

  // Navigate to appointment detail / scribe page
  const handleOpenAppointment = (appointmentId) => {
    navigate(`/practice-view/ambient-scribe/appointment/${appointmentId}`);
  };

  // Session history note review
  const loadSessionNotes = async (session) => {
    setSelectedSession(session);
    setActiveTab('detail');
    // Opening a different session must not show the previous one's transcript.
    setDetailPane('note');
    setTranscript(null);
    setTranscriptError(null);
    setNotesLoading(true);
    try {
      const res = await api.getScribeNotes(practiceId, session.guid);
      setSessionNotes(res.data || null);
    } catch (err) {
      console.error('Failed to load notes:', err);
      setSessionNotes(null);
    } finally {
      setNotesLoading(false);
    }
  };

  // Fetched on first view rather than alongside the notes: a transcript is a
  // full S3 round-trip and most note reviews never open it.
  const loadTranscript = useCallback(async (session) => {
    if (!session) return;
    setTranscriptLoading(true);
    setTranscriptError(null);
    try {
      const res = await api.getScribeTranscript(practiceId, session.guid);
      setTranscript(res.data?.transcript ?? null);
    } catch (err) {
      console.error('Failed to load transcript:', err);
      setTranscript(null);
      setTranscriptError(err.message || 'Could not load the transcript.');
    } finally {
      setTranscriptLoading(false);
    }
  }, [practiceId]);

  const handleShowTranscript = () => {
    setDetailPane('transcript');
    if (transcript === null && !transcriptError && !transcriptLoading) {
      loadTranscript(selectedSession);
    }
  };

  const handleSaveNotes = async (sections) => {
    if (!selectedSession) return;
    try {
      setNotesSaving(true);
      await api.updateScribeNotes(practiceId, selectedSession.guid, sections);
      const res = await api.getScribeNotes(practiceId, selectedSession.guid);
      setSessionNotes(res.data || null);
    } catch (err) {
      alert('Failed to save notes: ' + err.message);
    } finally {
      setNotesSaving(false);
    }
  };

  const handleApproveNotes = async () => {
    if (!selectedSession) return;
    if (!window.confirm('Approve these notes? This action locks the notes for clinical use.')) return;
    try {
      await api.approveScribeNotes(practiceId, selectedSession.guid);
      const res = await api.getScribeNotes(practiceId, selectedSession.guid);
      setSessionNotes(res.data || null);
    } catch (err) {
      alert('Failed to approve notes: ' + err.message);
    }
  };

  const handleRejectNotes = async () => {
    if (!selectedSession) return;
    if (!window.confirm('Reject these notes? You can re-edit and resubmit later.')) return;
    try {
      await api.rejectScribeNotes(practiceId, selectedSession.guid);
      const res = await api.getScribeNotes(practiceId, selectedSession.guid);
      setSessionNotes(res.data || null);
    } catch (err) {
      alert('Failed to reject notes: ' + err.message);
    }
  };

  // Pushing an approved note to the EHR was unreachable from this page. NoteReview
  // renders the button only when it is given onPushToEHR, and this page rendered it
  // without that prop — so a note opened from Session History could be approved and
  // then never pushed, while the same note opened from the appointment page could.
  // The endpoint and the API method both already existed; only the wiring was missing.
  // Mirrors AppointmentScribe.handlePushToEHR, which has worked all along.
  const handlePushToEHR = async () => {
    if (!selectedSession) return;
    if (!window.confirm('Push these notes to the EHR? This will update the encounter assessment.')) return;
    try {
      setSessionNotes(prev => (prev ? { ...prev, ehrPushStatus: 'pushing' } : prev));
      await api.pushScribeNotesToEHR(practiceId, selectedSession.guid);
      const res = await api.getScribeNotes(practiceId, selectedSession.guid);
      setSessionNotes(res.data || null);
    } catch (err) {
      // Reload so the failed status from the server replaces the optimistic one.
      try {
        const res = await api.getScribeNotes(practiceId, selectedSession.guid);
        setSessionNotes(res.data || null);
      } catch { /* keep the optimistic state if the reload also fails */ }
      alert('Failed to push notes to EHR: ' + err.message);
    }
  };

  const handleBackToList = () => {
    setSelectedSession(null);
    setSessionNotes(null);
    setActiveTab('appointments');
    loadStats();
  };

  if (!practiceId) {
    return (
      <div style={styles.noPractice}>
        <div style={styles.noPracticeCard}>
          <h3 style={styles.noPracticeTitle}>No Practice Selected</h3>
          <p style={styles.noPracticeText}>Please select a practice to use Ambient Scribe.</p>
        </div>
      </div>
    );
  }

  // Session detail / note review view (from session history tab)
  if (activeTab === 'detail' && selectedSession) {
    return (
      <div style={styles.pageContainer}>
        <div style={styles.detailHeader}>
          <button style={styles.backBtn} onClick={handleBackToList}>
            <BackIcon /> Back
          </button>
          <div>
            <h2 style={styles.detailTitle}>
              {selectedSession.patient_first_name} {selectedSession.patient_last_name}
            </h2>
            <span style={styles.detailMeta}>
              {selectedSession.specialty?.replace(/_/g, ' ')} |{' '}
              {selectedSession.note_template?.replace(/_/g, ' ')} |{' '}
              {formatDuration(selectedSession.duration_seconds)}
            </span>
          </div>
        </div>
        <div style={styles.detailTabs}>
          <button
            type="button"
            style={{ ...styles.detailTab, ...(detailPane === 'note' ? styles.detailTabActive : {}) }}
            onClick={() => setDetailPane('note')}
            aria-pressed={detailPane === 'note'}
          >
            Note
          </button>
          <button
            type="button"
            style={{ ...styles.detailTab, ...(detailPane === 'transcript' ? styles.detailTabActive : {}) }}
            onClick={handleShowTranscript}
            aria-pressed={detailPane === 'transcript'}
          >
            Transcript
          </button>
        </div>
        <div style={styles.detailCard}>
          {detailPane === 'transcript' ? (
            <SessionTranscript
              transcript={transcript}
              loading={transcriptLoading}
              error={transcriptError}
              onRetry={() => loadTranscript(selectedSession)}
            />
          ) : notesLoading ? (
            <div style={styles.loading}>Loading notes...</div>
          ) : (
            <NoteReview
              notes={sessionNotes?.notes}
              noteStatus={sessionNotes?.noteStatus}
              onSave={handleSaveNotes}
              onApprove={handleApproveNotes}
              onReject={handleRejectNotes}
              saving={notesSaving}
              onPushToEHR={handlePushToEHR}
              ehrPushStatus={sessionNotes?.ehrPushStatus}
              ehrPushError={sessionNotes?.ehrPushError}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.pageContainer}>
      {/* Stats Bar */}
      {stats && (
        <div style={styles.statsBar}>
          <StatCard label="Total Sessions" value={stats.total_sessions || 0} color="#3b82f6" />
          <StatCard label="Audio Minutes" value={Math.round(stats.total_audio_minutes || 0)} color="#8b5cf6" />
          <StatCard label="Approved" value={stats.approved_notes || 0} color="#10b981" />
          <StatCard label="Est. Cost" value={`$${(stats.total_estimated_cost || 0).toFixed(2)}`} color="#6b7280" />
        </div>
      )}

      {/* Tab Bar */}
      <div style={styles.tabBar}>
        <button
          style={{ ...styles.tab, ...(activeTab === 'appointments' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('appointments')}
        >
          <CalendarIcon /> Today's Appointments
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'sessions' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('sessions')}
        >
          <ListIcon /> Session History
        </button>
      </div>

      {/* Content */}
      <div style={styles.mainContent}>
        {activeTab === 'appointments' && (() => {
          // GH-184: filter + paginate the list. Search matches name, phone,
          // and appointment_type (case-insensitive substring). Reset to
          // page 1 whenever the query changes.
          const q = (searchQuery || '').trim().toLowerCase();
          const filtered = q
            ? appointments.filter(a => {
                const hay = [
                  a.first_name, a.last_name, a.patient_first_name, a.patient_last_name,
                  a.patient_phone, a.phone, a.appointment_type, a.provider_name,
                ].filter(Boolean).join(' ').toLowerCase();
                return hay.includes(q);
              })
            : appointments;
          const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
          const currentPage = Math.min(page, totalPages);
          const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
          return (
          <div>
            {/* GH-184: search box + result counter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
              <input
                type="search"
                placeholder="Search by patient name, phone, or type…"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                style={{
                  flex: '1 1 300px',
                  padding: '8px 12px',
                  fontSize: 14,
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  outline: 'none',
                }}
              />
              <span style={{ fontSize: 12, color: '#6b7280' }}>
                {filtered.length} of {appointments.length} shown
              </span>
            </div>
            {appointmentsLoading ? (
              <div style={styles.loading}>Loading appointments...</div>
            ) : appointments.length === 0 ? (
              <div style={styles.emptyState}>
                <CalendarIcon large />
                <p style={styles.emptyTitle}>No Appointments Today</p>
                <p style={styles.emptyText}>
                  {user?.role === 'care_provider'
                    ? 'You have no appointments scheduled for today.'
                    : 'No appointments found for today.'}
                </p>
              </div>
            ) : filtered.length === 0 ? (
              <div style={styles.emptyState}>
                <p style={styles.emptyTitle}>No matches</p>
                <p style={styles.emptyText}>No appointments match "{searchQuery}". Clear the search to see all.</p>
              </div>
            ) : (
              <div style={styles.appointmentList}>
                <div style={styles.appointmentHeader}>
                  <span style={{ ...styles.colHeader, flex: '0 0 90px' }}>Time</span>
                  <span style={{ ...styles.colHeader, flex: '1' }}>Patient</span>
                  <span style={{ ...styles.colHeader, flex: '0 0 140px' }}>Type</span>
                  <span style={{ ...styles.colHeader, flex: '0 0 70px' }}>Dur.</span>
                  <span style={{ ...styles.colHeader, flex: '0 0 100px' }}>Status</span>
                  <span style={{ ...styles.colHeader, flex: '0 0 80px' }}>Scribe</span>
                  <span style={{ ...styles.colHeader, flex: '0 0 130px' }}>Action</span>
                </div>
                {paged.map(appt => {
                  const scribeSession = scribeSessionMap[appt.id];
                  const isCheckedIn = appt.status === 'checked-in';
                  const hasCompletedScribe = scribeSession && scribeSession.status === 'completed';
                  const hasActiveScribe = scribeSession && ['initializing', 'streaming', 'paused', 'processing'].includes(scribeSession.status);
                  // A failed session (e.g. HealthScribe refused the stream) used to render as '--'
                  // with no way to see why; GH-184 sat for months because of it.
                  const hasFailedScribe = scribeSession && ['failed', 'cancelled'].includes(scribeSession.status);
                  const statusColor = STATUS_COLORS[appt.status] || STATUS_COLORS.scheduled;

                  return (
                    <div key={appt.id} style={styles.appointmentRow} onClick={() => handleOpenAppointment(appt.id)}>
                      <span style={{ ...styles.col, flex: '0 0 90px', fontWeight: 500 }}>
                        {formatApptTime(pickApptTime(appt))}
                      </span>
                      <span style={{ ...styles.col, flex: '1', flexDirection: 'column', alignItems: 'flex-start', gap: 0 }}>
                        <span style={styles.patientName}>
                          {appt.first_name || ''} {appt.last_name || ''}
                          {!appt.first_name && !appt.last_name && <em style={{ color: '#9ca3af' }}>No patient</em>}
                        </span>
                        {appt.patient_phone && (
                          <span style={styles.patientPhone}>{appt.patient_phone}</span>
                        )}
                      </span>
                      <span style={{ ...styles.col, flex: '0 0 140px', fontSize: '13px', color: '#6b7280' }}>
                        {appt.appointment_type || '--'}
                      </span>
                      <span style={{ ...styles.col, flex: '0 0 70px', fontSize: '13px', color: '#6b7280' }}>
                        {appt.duration_minutes ? `${appt.duration_minutes}m` : '--'}
                      </span>
                      <span style={{ ...styles.col, flex: '0 0 100px' }}>
                        <span style={{
                          ...styles.badge,
                          backgroundColor: statusColor.bg,
                          color: statusColor.text
                        }}>
                          {statusColor.label}
                        </span>
                      </span>
                      <span style={{ ...styles.col, flex: '0 0 80px' }}>
                        {hasCompletedScribe && scribeSession.ehr_push_status === 'pushed' && (
                          <span style={styles.scribePushed}>Pushed</span>
                        )}
                        {hasCompletedScribe && scribeSession.ehr_push_status === 'failed' && (
                          <span style={styles.scribePushFailed}>Push Failed</span>
                        )}
                        {hasCompletedScribe && scribeSession.note_status === 'approved' && !scribeSession.ehr_push_status && (
                          <span style={styles.scribeApproved}>Approved</span>
                        )}
                        {hasCompletedScribe && !['approved', 'pushed_to_ehr'].includes(scribeSession.note_status) && !scribeSession.ehr_push_status && (
                          <span style={styles.scribeDone}>Done</span>
                        )}
                        {hasActiveScribe && <span style={styles.scribeActive} title={scribeSession.error_message || ''}>Active</span>}
                        {hasFailedScribe && (
                          <span style={styles.scribePushFailed} title={scribeSession.error_message || ''}>
                            {scribeSession.status === 'failed' ? 'Failed' : 'Cancelled'}
                          </span>
                        )}
                        {!scribeSession && <span style={styles.scribeNone}>--</span>}
                      </span>
                      <span style={{ ...styles.col, flex: '0 0 130px' }}>
                        {isCheckedIn && !scribeSession && (
                          <button style={styles.startScribeBtn} onClick={() => handleOpenAppointment(appt.id)}>
                            <MicIcon /> Start Scribe
                          </button>
                        )}
                        {(hasCompletedScribe || hasActiveScribe) && (
                          <button style={styles.viewNotesBtn} onClick={() => handleOpenAppointment(appt.id)}>
                            {hasCompletedScribe ? 'Review Notes' : 'View Session'}
                          </button>
                        )}
                        {isCheckedIn && hasFailedScribe && (
                          <button style={styles.startScribeBtn} onClick={() => handleOpenAppointment(appt.id)} title={scribeSession.error_message || ''}>
                            <MicIcon /> Retry Scribe
                          </button>
                        )}
                        {!isCheckedIn && !scribeSession && (
                          <span style={{ fontSize: '12px', color: '#d1d5db' }}>--</span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
            {/* GH-184: pagination controls (only show when needed) */}
            {filtered.length > PAGE_SIZE && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 16 }}>
                <button
                  type="button"
                  onClick={() => setPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage <= 1}
                  style={{
                    padding: '6px 12px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 6,
                    background: currentPage <= 1 ? '#f3f4f6' : '#fff',
                    cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
                    color: currentPage <= 1 ? '#9ca3af' : '#111',
                  }}
                >← Prev</button>
                <span style={{ fontSize: 13, color: '#6b7280' }}>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage >= totalPages}
                  style={{
                    padding: '6px 12px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 6,
                    background: currentPage >= totalPages ? '#f3f4f6' : '#fff',
                    cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
                    color: currentPage >= totalPages ? '#9ca3af' : '#111',
                  }}
                >Next →</button>
              </div>
            )}
          </div>
          );
        })()}

        {activeTab === 'sessions' && (
          <ScribeSessionList
            practiceId={practiceId}
            onSelectSession={loadSessionNotes}
          />
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div style={styles.statCard}>
      <div style={{ ...styles.statValue, color }}>{value}</div>
      <div style={styles.statLabel}>{label}</div>
    </div>
  );
}

function formatDuration(seconds) {
  if (!seconds) return '--';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}

function BackIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function ListIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="8" y1="6" x2="21" y2="6" />
      <line x1="8" y1="12" x2="21" y2="12" />
      <line x1="8" y1="18" x2="21" y2="18" />
      <line x1="3" y1="6" x2="3.01" y2="6" />
      <line x1="3" y1="12" x2="3.01" y2="12" />
      <line x1="3" y1="18" x2="3.01" y2="18" />
    </svg>
  );
}

function CalendarIcon({ large }) {
  const size = large ? 40 : 16;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={large ? '#9ca3af' : 'currentColor'} strokeWidth={large ? 1.5 : 2}>
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function MicIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
      <path d="M19 10v2a7 7 0 01-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </svg>
  );
}

const styles = {
  pageContainer: {
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '100%',
    boxSizing: 'border-box'
  },
  noPractice: { display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px 20px' },
  noPracticeCard: { textAlign: 'center', padding: '40px', backgroundColor: '#fff', borderRadius: '10px', border: '1px solid #e5e7eb' },
  noPracticeTitle: { margin: '0 0 8px', fontSize: '18px', fontWeight: 600, color: '#6b7280' },
  noPracticeText: { margin: 0, fontSize: '14px', color: '#9ca3af' },
  statsBar: { display: 'flex', gap: '12px', flexWrap: 'wrap' },
  statCard: { flex: '1 1 120px', padding: '14px 16px', backgroundColor: '#fff', borderRadius: '10px', border: '1px solid #e5e7eb', textAlign: 'center' },
  statValue: { fontSize: '24px', fontWeight: 700 },
  statLabel: { fontSize: '12px', color: '#6b7280', marginTop: '2px' },
  tabBar: { display: 'flex', gap: '4px', backgroundColor: '#fff', padding: '4px', borderRadius: '10px', border: '1px solid #e5e7eb' },
  tab: { display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 20px', borderRadius: '8px', border: 'none', backgroundColor: 'transparent', color: '#6b7280', fontSize: '14px', fontWeight: 500, cursor: 'pointer' },
  tabActive: { backgroundColor: '#f3f4f6', color: '#111827', fontWeight: 600 },
  mainContent: { backgroundColor: '#fff', borderRadius: '10px', border: '1px solid #e5e7eb', padding: '16px', minHeight: '400px' },
  loading: { padding: '40px', textAlign: 'center', color: '#6b7280', fontSize: '14px' },
  emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '60px 20px', color: '#9ca3af' },
  emptyTitle: { margin: 0, fontSize: '16px', fontWeight: 600, color: '#6b7280' },
  emptyText: { margin: 0, fontSize: '14px', textAlign: 'center' },
  // Appointment table
  appointmentList: { display: 'flex', flexDirection: 'column' },
  appointmentHeader: { display: 'flex', alignItems: 'center', padding: '8px 12px', borderBottom: '2px solid #e5e7eb', marginBottom: '4px' },
  colHeader: { fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' },
  appointmentRow: { display: 'flex', alignItems: 'center', padding: '10px 12px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', transition: 'background-color 0.15s ease' },
  col: { fontSize: '14px', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '4px' },
  patientName: { fontWeight: 500, fontSize: '14px', display: 'block' },
  patientPhone: { fontSize: '12px', color: '#6b7280', display: 'block' },
  badge: { padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600 },
  scribeDone: { fontSize: '12px', color: '#065f46', fontWeight: 600 },
  scribeApproved: { fontSize: '12px', color: '#2563eb', fontWeight: 600 },
  scribePushed: { fontSize: '12px', color: '#065f46', fontWeight: 600 },
  scribePushFailed: { fontSize: '12px', color: '#dc2626', fontWeight: 600 },
  scribeActive: { fontSize: '12px', color: '#1e40af', fontWeight: 600 },
  scribeNone: { fontSize: '12px', color: '#d1d5db' },
  startScribeBtn: { display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 12px', borderRadius: '6px', border: 'none', backgroundColor: '#059669', color: '#fff', fontSize: '12px', fontWeight: 500, cursor: 'pointer' },
  viewNotesBtn: { padding: '5px 12px', borderRadius: '6px', border: '1px solid #d1d5db', backgroundColor: '#fff', color: '#374151', fontSize: '12px', fontWeight: 500, cursor: 'pointer' },
  // Detail view (session history)
  detailHeader: { display: 'flex', alignItems: 'center', gap: '16px', padding: '16px 20px', backgroundColor: '#fff', borderRadius: '10px', border: '1px solid #e5e7eb' },
  backBtn: { display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 14px', borderRadius: '6px', border: '1px solid #d1d5db', backgroundColor: '#fff', color: '#374151', fontSize: '13px', cursor: 'pointer', fontWeight: 500 },
  detailTitle: { margin: 0, fontSize: '18px', fontWeight: 700, color: '#111827' },
  detailMeta: { fontSize: '13px', color: '#6b7280', textTransform: 'capitalize' },
  detailTabs: { display: 'flex', gap: '4px', marginBottom: '12px' },
  detailTab: {
    padding: '8px 16px', fontSize: '14px', fontWeight: 500, cursor: 'pointer',
    background: 'transparent', border: 'none', borderBottom: '2px solid transparent', color: '#6b7280'
  },
  detailTabActive: { color: '#2563eb', borderBottom: '2px solid #2563eb' },
  detailCard: { backgroundColor: '#fff', borderRadius: '10px', border: '1px solid #e5e7eb', minHeight: '300px', padding: '16px' }
};

export default AmbientScribe;
